import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
  CircularProgress
} from "@mui/material";
import { ChangeEvent, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EditText from "../../components/editText";
import { useUploadMediaMutation } from "../../service/common/api";
import { useUploadBlogImagesMutation, useAddBlogMutation, useUpdateBlogMutation, useGetBlogByIdQuery } from "../../service/blogs/api";
import { showToast, showError } from "../../constants/toasts";

const AddEditBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isAddBlog = !id;

  const { hasPermission } = usePermission();
  const hasAddPermission = hasPermission("Blog Management", "isAdd");
  const hasEditPermission = hasPermission("Blog Management", "isEdit");
  const canSave = isAddBlog ? hasAddPermission : hasEditPermission;

  const [blogBannerFile, setBlogBannerFile] = useState<File | null>(null);
  const [blogBannerPreview, setBlogBannerPreview] = useState<string>("");
  
  const [blogImagesFiles, setBlogImagesFiles] = useState<File[]>([]);
  const [blogImagesPreviews, setBlogImagesPreviews] = useState<string[]>([]);
  
  const [authorImageFile, setAuthorImageFile] = useState<File | null>(null);
  const [authorImagePreview, setAuthorImagePreview] = useState<string>("");

  const [blogTitle, setBlogTitle] = useState("");
  const [blogTitleAr, setBlogTitleAr] = useState("");
  const [blogDescription, setBlogDescription] = useState("");
  const [blogDescriptionAr, setBlogDescriptionAr] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [blogContentAr, setBlogContentAr] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorNameAr, setAuthorNameAr] = useState("");

  const [uploadMedia, { isLoading: isUploadingMedia }] = useUploadMediaMutation();
  const [uploadBlogImages, { isLoading: isUploadingBlogImages }] = useUploadBlogImagesMutation();
  const [addBlog, { isLoading: isAddingBlog }] = useAddBlogMutation();
  const [updateBlog, { isLoading: isUpdatingBlog }] = useUpdateBlogMutation();

  const { data: blogData, isLoading: isFetchingBlog } = useGetBlogByIdQuery(id, { skip: !id });

  useEffect(() => {
    if (!isAddBlog && blogData?.data) {
      const blog = blogData.data;
      setBlogTitle(blog.blogTitle || "");
      setBlogTitleAr(blog.blogTitle_ar || "");
      setBlogDescription(blog.blogDescription || "");
      setBlogDescriptionAr(blog.blogDescription_ar || "");
      setBlogContent(blog.blogContent || "");
      setBlogContentAr(blog.blogContent_ar || "");
      setAuthorName(blog.authorName || "");
      setAuthorNameAr(blog.authorName_ar || "");
      if (blog.blogBannerImage) {
        setBlogBannerPreview(blog.blogBannerImage);
      }
      if (blog.authorImage) {
        setAuthorImagePreview(blog.authorImage);
      }
      if (blog.blogImages && blog.blogImages.length > 0) {
        setBlogImagesPreviews(blog.blogImages);
      }
    }
  }, [blogData, isAddBlog]);

  const handleSave = async () => {
    try {
      let bannerUrl = blogBannerPreview;
      if (blogBannerFile) {
        const formData = new FormData();
        formData.append("file", blogBannerFile);
        const res = await uploadMedia(formData).unwrap();
        if (res?.data) {
          bannerUrl = res.data;
        }
      }

      let authorUrl = authorImagePreview;
      if (authorImageFile) {
        const formData = new FormData();
        formData.append("file", authorImageFile);
        const res = await uploadMedia(formData).unwrap();
        if (res?.data) {
          authorUrl = res.data;
        }
      }

      // Preserve existing image previews that weren't deleted
      let blogImagesUrls: string[] = blogImagesPreviews.filter(img => !img.startsWith('blob:'));
      if (blogImagesFiles.length > 0) {
        const formData = new FormData();
        blogImagesFiles.forEach(file => {
          formData.append("files", file);
        });
        const res = await uploadBlogImages(formData).unwrap();
        if (res?.data) {
          blogImagesUrls = [...blogImagesUrls, ...res.data];
          showToast("Images uploaded successfully!");
        }
      }

      const payload = {
        blogTitle,
        blogTitle_ar: blogTitleAr,
        blogDescription,
        blogDescription_ar: blogDescriptionAr,
        blogContent,
        blogContent_ar: blogContentAr,
        authorName,
        authorName_ar: authorNameAr,
        blogBannerImage: bannerUrl,
        blogImages: blogImagesUrls,
        authorImage: authorUrl,
        isActive: true,
      };

      if (isAddBlog) {
        await addBlog(payload).unwrap();
        showToast("Blog created successfully!");
      } else {
        await updateBlog({ id, ...payload }).unwrap();
        showToast("Blog updated successfully!");
      }
      
      navigate("/manage-blogs-listings");
    } catch (err) {
      console.error(err);
      showError("Something went wrong while saving the blog.");
    }
  };

  const isLoading = isUploadingMedia || isUploadingBlogImages || isAddingBlog || isUpdatingBlog || isFetchingBlog;

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={`${isAddBlog ? "Add" : "Edit"} Blog`}
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Blog Management", onClick: () => navigate("/manage-blogs-listings") },
          { label: `${isAddBlog ? "Add" : "Edit"} Blog` },
        ]}
      />

      <div className="form">

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Blog Information (معلومات المدونة)</h2>
          </div>

          <div className="gap_p">
            {/* Blog Banner Upload */}
            <div className="control_group w_100">
              <label>Blog Banner (شعار المدونة)</label>
              <div className="gap_p">
                <div className="w_25">
                  {blogBannerPreview ? (
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={blogBannerPreview} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            setBlogBannerPreview("");
                            setBlogBannerFile(null);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <label className="upload_image no_radius v2">
                      <Input
                        sx={{ display: "none" }}
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const files = event.target.files;
                          if (files && files[0]) {
                            setBlogBannerPreview(URL.createObjectURL(files[0]));
                            setBlogBannerFile(files[0]);
                          }
                        }}
                      />
                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img
                            src="/static/images/placeholder.png"
                            alt=""
                          />
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt=""
                            />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Blog Images Upload */}
            <div className="control_group w_100">
              <label>Blog Images (صور المدونة)</label>
              <div className="gap_p" style={{ flexWrap: 'wrap' }}>
                {blogImagesPreviews.map((img, index) => (
                  <div className="w_25" key={index}>
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={img} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            setBlogImagesPreviews(prev => prev.filter((_, i) => i !== index));
                            setBlogImagesFiles(prev => prev.filter((_, i) => i !== index));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="w_25">
                  <label className="upload_image no_radius v2">
                    <Input
                      sx={{ display: "none" }}
                      type="file"
                      inputProps={{ accept: "image/*", multiple: true }}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const files = event.target.files;
                        if (files) {
                          const newPreviews = Array.from(files).map(file => URL.createObjectURL(file));
                          setBlogImagesPreviews(prev => [...prev, ...newPreviews]);
                          setBlogImagesFiles(prev => [...prev, ...Array.from(files)]);
                        }
                      }}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt=""
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt=""
                          />
                        </span>
                      </figure>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Blog Title */}
            <div style={{ display: "flex", gap: 16, width: "100%" }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Blog Title</label>
                <TextField placeholder="Enter Blog Title" hiddenLabel fullWidth value={blogTitle} onChange={(e) => setBlogTitle(e.target.value)} />
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>عنوان المدونة</label>
                <TextField placeholder="Enter Blog Title in Arabic" hiddenLabel fullWidth value={blogTitleAr} onChange={(e) => setBlogTitleAr(e.target.value)} />
              </div>
            </div>

            {/* Blog Description */}
            <div style={{ display: "flex", gap: 16, width: "100%" }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Blog Description</label>
                <TextField placeholder="Enter Blog Description" hiddenLabel fullWidth multiline rows={4} value={blogDescription} onChange={(e) => setBlogDescription(e.target.value)} />
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>وصف المدونة</label>
                <TextField placeholder="Enter Blog Description in Arabic" hiddenLabel fullWidth multiline rows={4} value={blogDescriptionAr} onChange={(e) => setBlogDescriptionAr(e.target.value)} />
              </div>
            </div>

            {/* Blog Content */}
            <div style={{ display: "flex", gap: 16, width: "100%" }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Blog Content</label>
                <EditText content={blogContent} setContent={setBlogContent} />
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>محتوى المدونة</label>
                <EditText content={blogContentAr} setContent={setBlogContentAr} />
              </div>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Author Detail (تفاصيل المؤلف)</h2>
          </div>

          <div className="gap_p">
            {/* Author Image Upload */}
            <div className="control_group w_100">
              <label>Author Image (صورة المؤلف)</label>
              <div className="gap_p">
                <div className="w_25">
                  {authorImagePreview ? (
                    <div className="upload_image">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={authorImagePreview} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            setAuthorImagePreview("");
                            setAuthorImageFile(null);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <label className="upload_image">
                      <Input
                        sx={{ display: "none" }}
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const files = event.target.files;
                          if (files && files[0]) {
                            setAuthorImagePreview(URL.createObjectURL(files[0]));
                            setAuthorImageFile(files[0]);
                          }
                        }}
                      />
                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img
                            src="/static/images/placeholder.png"
                            alt=""
                          />
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt=""
                            />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Author Name */}
            <div style={{ display: "flex", gap: 16, width: "100%" }}>
              <div className="control_group" style={{ flex: 1 }}>
                <label>Author Name</label>
                <TextField placeholder="Enter Author Name" hiddenLabel fullWidth value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
              </div>
              <div className="control_group" style={{ flex: 1 }}>
                <label>اسم المؤلف</label>
                <TextField placeholder="Enter Author Name in Arabic" hiddenLabel fullWidth value={authorNameAr} onChange={(e) => setAuthorNameAr(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary" disabled={!canSave || isLoading} onClick={handleSave}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save (يحفظ)"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddEditBlog;
